import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { name: "", phone: "", email: "", source: "", type: "sin_clasificar", notes: "", potential_distributor: false };

export function ContactDialog({ open, onOpenChange, onSaved, contact }) {
    const editing = Boolean(contact);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (open) {
            setForm(contact ? { ...EMPTY, ...contact } : EMPTY);
            setErrors({});
        }
    }, [open, contact]);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        const e = {};
        if (!form.name || form.name.trim().length < 2) e.name = "Escribe el nombre del contacto.";
        if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = "Ese correo no parece válido.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async (ev) => {
        ev.preventDefault();
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                phone: form.phone?.trim() || null,
                email: form.email?.trim() || null,
                source: form.source?.trim() || null,
                type: form.type,
                notes: form.notes?.trim() || null,
                potential_distributor: !!form.potential_distributor,
            };
            let data;
            if (editing) {
                ({ data } = await api.put(`/contacts/${contact.id}`, payload));
                toast.success("Contacto actualizado");
            } else {
                ({ data } = await api.post("/contacts", payload));
                toast.success("Contacto añadido");
            }
            onSaved?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg rounded-2xl" data-testid="contact-dialog">
                <DialogHeader>
                    <DialogTitle className="text-xl">{editing ? "Editar contacto" : "Nuevo contacto"}</DialogTitle>
                    <DialogDescription className="text-nm-muted">
                        {editing ? "Actualiza los datos de esta persona." : "Añade a alguien de tu lista. Lo organizamos por ti."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="space-y-1.5">
                        <Label htmlFor="c-name">Nombre</Label>
                        <Input id="c-name" data-testid="contact-name-input" value={form.name}
                            onChange={(e) => set("name", e.target.value)} placeholder="Nombre y apellidos" />
                        {errors.name && <p className="text-xs text-destructive" data-testid="contact-name-error">{errors.name}</p>}
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="c-phone">Teléfono</Label>
                            <Input id="c-phone" data-testid="contact-phone-input" value={form.phone || ""}
                                onChange={(e) => set("phone", e.target.value)} placeholder="+34 ..." />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="c-email">Correo</Label>
                            <Input id="c-email" data-testid="contact-email-input" value={form.email || ""}
                                onChange={(e) => set("email", e.target.value)} placeholder="nombre@correo.com" />
                            {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
                        </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="c-source">¿De dónde salió?</Label>
                            <Input id="c-source" data-testid="contact-source-input" value={form.source || ""}
                                onChange={(e) => set("source", e.target.value)} placeholder="Instagram, referido..." />
                        </div>
                        <div className="space-y-1.5">
                            <Label>¿Cliente o equipo?</Label>
                            <Select value={form.type} onValueChange={(v) => set("type", v)}>
                                <SelectTrigger data-testid="contact-type-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="sin_clasificar">Sin clasificar</SelectItem>
                                    <SelectItem value="cliente">Posible cliente</SelectItem>
                                    <SelectItem value="equipo">Posible equipo</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="c-notes">Notas</Label>
                        <Textarea id="c-notes" data-testid="contact-notes-input" rows={3} value={form.notes || ""}
                            onChange={(e) => set("notes", e.target.value)} placeholder="Qué te dijo, qué le interesa..." />
                    </div>
                    <label className="flex items-center gap-2.5 text-sm text-nm-ink">
                        <Checkbox checked={!!form.potential_distributor}
                            onCheckedChange={(v) => set("potential_distributor", v)} data-testid="contact-distributor-checkbox" />
                        Posible distribuidor del producto
                    </label>
                    <div className="flex justify-end gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                        <Button type="submit" disabled={saving} data-testid="contact-save-button">
                            {saving ? "Guardando..." : editing ? "Guardar cambios" : "Añadir contacto"}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
